import { useEffect, useState, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  LogOut,
  Home,
  Users,
  Calendar,
  UserSquare2,
  Mail,
  Inbox,
  LayoutDashboard,
  Plus,
  Trash2,
  Save,
  X,
  Pencil,
  Check,
  Loader2,
} from "lucide-react";
import api, { formatApiError } from "../api";
import { useAuth } from "../auth";
import { useI18n } from "../i18n";
import { HelpButton } from "../components/AdminHelp";

// ============ Overview Tab ============
const OverviewTab = () => {
  const { t } = useI18n();
  const [stats, setStats] = useState(null);

  useEffect(() => {
    api
      .get("/admin/stats")
      .then((r) => setStats(r.data))
      .catch((e) => toast.error(formatApiError(e)));
  }, []);

  if (!stats) return <div className="text-white/40 py-12">Loading...</div>;

  const cards = [
    { key: "shows", icon: Calendar },
    { key: "team", icon: UserSquare2 },
    { key: "bookings", icon: Inbox },
    { key: "newsletter", icon: Mail },
    { key: "users_pending", icon: Users },
    { key: "users_total", icon: Users },
  ];

  return (
    <div className="grid grid-cols-2 lg:grid-cols-3 gap-px bg-white/15 border border-white/15">
      {cards.map(({ key, icon: Icon }) => (
        <div
          key={key}
          className="bg-[#0a0a0a] p-6 md:p-8"
          data-testid={`stat-${key}`}
        >
          <div className="flex items-center justify-between mb-4 text-white/40">
            <Icon size={18} />
            <span className="text-[10px] tracking-widest uppercase">
              {t.admin.stats[key]}
            </span>
          </div>
          <div className="font-display text-6xl md:text-7xl tracking-tighter">
            {stats[key]}
          </div>
        </div>
      ))}
    </div>
  );
};

// ============ Shows Tab ============
const emptyShow = () => ({
  date: "",
  city: "",
  country: "",
  country_code: "",
  venue: "",
  venue_address: "",
  ticket_url: "",
  status: "on_sale",
});

const ShowsTab = () => {
  const { t } = useI18n();
  const [shows, setShows] = useState([]);
  const [editing, setEditing] = useState(null); // id or "new"
  const [draft, setDraft] = useState(emptyShow());

  const load = useCallback(async () => {
    try {
      const r = await api.get("/shows");
      setShows(r.data);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const startEdit = (s) => {
    setEditing(s.id);
    setDraft({ ...s });
  };
  const startNew = () => {
    setEditing("new");
    setDraft(emptyShow());
  };
  const cancel = () => {
    setEditing(null);
    setDraft(emptyShow());
  };

  const save = async () => {
    try {
      if (editing === "new") {
        await api.post("/admin/shows", draft);
        toast.success("Created");
      } else {
        await api.patch(`/admin/shows/${editing}`, draft);
        toast.success("Saved");
      }
      await load();
      cancel();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const remove = async (id) => {
    if (!window.confirm(t.admin.shows.confirm_delete)) return;
    try {
      await api.delete(`/admin/shows/${id}`);
      toast.success("Deleted");
      await load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  return (
    <div className="space-y-6" data-testid="admin-shows">
      <div className="flex justify-end">
        <button
          onClick={startNew}
          className="btn-primary"
          data-testid="admin-shows-add"
        >
          <Plus size={16} /> {t.admin.shows.add}
        </button>
      </div>

      <div className="border border-white/15">
        {editing === "new" && (
          <ShowEditor
            draft={draft}
            setDraft={setDraft}
            onSave={save}
            onCancel={cancel}
            t={t}
          />
        )}
        {shows.map((s) =>
          editing === s.id ? (
            <ShowEditor
              key={s.id}
              draft={draft}
              setDraft={setDraft}
              onSave={save}
              onCancel={cancel}
              t={t}
            />
          ) : (
            <div
              key={s.id}
              className="grid grid-cols-12 gap-3 items-center px-4 py-4 border-b border-white/10 last:border-b-0 hover:bg-white/[0.03]"
              data-testid={`admin-show-row-${s.id}`}
            >
              <div className="col-span-12 md:col-span-2 text-sm text-white/80">
                {new Date(s.date).toLocaleDateString("en-US", {
                  day: "2-digit",
                  month: "short",
                  year: "numeric",
                })}
              </div>
              <div className="col-span-6 md:col-span-2 font-display text-xl">
                {s.city}
              </div>
              <div className="col-span-6 md:col-span-1 text-xs tracking-widest text-white/50">
                {s.country_code}
              </div>
              <div className="col-span-12 md:col-span-3 text-sm text-white/70 truncate">
                {s.venue}
              </div>
              <div className="col-span-6 md:col-span-2">
                <span
                  className={`text-[10px] tracking-widest uppercase px-2 py-1 border ${
                    s.status === "sold_out"
                      ? "text-red-400 border-red-400/40"
                      : s.status === "low_tickets"
                      ? "text-[#FFDE00] border-[#FFDE00]/40"
                      : "text-white/70 border-white/20"
                  }`}
                >
                  {s.status}
                </span>
              </div>
              <div className="col-span-6 md:col-span-2 flex justify-end gap-2">
                <button
                  onClick={() => startEdit(s)}
                  className="p-2 border border-white/20 hover:border-[#FFDE00] hover:text-[#FFDE00] transition-colors"
                  data-testid={`admin-show-edit-${s.id}`}
                  title={t.admin.shows.edit}
                >
                  <Pencil size={14} />
                </button>
                <button
                  onClick={() => remove(s.id)}
                  className="p-2 border border-white/20 hover:border-red-400 hover:text-red-400 transition-colors"
                  data-testid={`admin-show-delete-${s.id}`}
                  title={t.admin.shows.delete}
                >
                  <Trash2 size={14} />
                </button>
              </div>
            </div>
          )
        )}
      </div>
    </div>
  );
};

const ShowEditor = ({ draft, setDraft, onSave, onCancel, t }) => {
  const upd = (k) => (e) => setDraft({ ...draft, [k]: e.target.value });
  const updUpper = (k) => (e) =>
    setDraft({ ...draft, [k]: (e.target.value || "").toUpperCase() });

  // --- Date / time split: parse draft.date (ISO) into separate date + time inputs ---
  const parseDraftDate = () => {
    const v = draft.date || "";
    // Expecting ISO "YYYY-MM-DDTHH:MM[:SS]"
    const m = v.match(/^(\d{4}-\d{2}-\d{2})(?:T(\d{2}:\d{2}))?/);
    if (m) return { date: m[1], time: m[2] || "19:00" };
    return { date: "", time: "19:00" };
  };
  const { date: dateOnly, time: timeOnly } = parseDraftDate();
  const setDateTime = (d, t2) => {
    const safeDate = d || "";
    const safeTime = (t2 || "19:00").length === 5 ? `${t2}:00` : t2 || "19:00:00";
    setDraft({
      ...draft,
      date: safeDate ? `${safeDate}T${safeTime}` : "",
    });
  };

  const textFields = [
    ["city", t.admin.shows.fields.city, "text"],
    ["country", t.admin.shows.fields.country, "text"],
    ["country_code", t.admin.shows.fields.country_code, "code"],
    ["venue", t.admin.shows.fields.venue, "text"],
    ["venue_address", t.admin.shows.fields.venue_address, "text"],
    ["ticket_url", t.admin.shows.fields.ticket_url, "url"],
  ];

  return (
    <div className="border-b border-white/10 p-5 bg-[#161616]/40" data-testid="admin-show-editor">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        {/* Date + time pair */}
        <div>
          <label className="text-[10px] tracking-widest uppercase text-white/45">
            {t.admin.shows.fields.date}
          </label>
          <input
            type="date"
            value={dateOnly}
            onChange={(e) => setDateTime(e.target.value, timeOnly)}
            className="field-input"
            data-testid="admin-show-input-date"
          />
        </div>
        <div>
          <label className="text-[10px] tracking-widest uppercase text-white/45">
            {t.admin.shows.fields.time || "Время начала"}
          </label>
          <input
            type="time"
            value={timeOnly?.slice(0, 5) || ""}
            onChange={(e) => setDateTime(dateOnly, e.target.value)}
            className="field-input"
            data-testid="admin-show-input-time"
          />
        </div>

        {textFields.map(([k, label, kind]) => (
          <div key={k}>
            <label className="text-[10px] tracking-widest uppercase text-white/45">
              {label}
            </label>
            <input
              type={kind === "url" ? "url" : "text"}
              value={draft[k] || ""}
              onChange={kind === "code" ? updUpper(k) : upd(k)}
              maxLength={kind === "code" ? 2 : undefined}
              placeholder={
                kind === "code"
                  ? "US, CA, KZ, DE, RU, AZ, UZ, KG"
                  : kind === "url"
                  ? "https://..."
                  : ""
              }
              className="field-input"
              data-testid={`admin-show-input-${k}`}
            />
          </div>
        ))}
        <div>
          <label className="text-[10px] tracking-widest uppercase text-white/45">
            {t.admin.shows.fields.status}
          </label>
          <select
            value={draft.status}
            onChange={upd("status")}
            className="field-input bg-[#0a0a0a]"
            data-testid="admin-show-input-status"
          >
            <option value="on_sale">on_sale — Билеты в продаже</option>
            <option value="low_tickets">low_tickets — Осталось мало</option>
            <option value="sold_out">sold_out — Распродано</option>
          </select>
        </div>
      </div>
      <div className="mt-5 flex gap-3">
        <button onClick={onSave} className="btn-primary" data-testid="admin-show-save">
          <Save size={14} /> {t.admin.shows.save}
        </button>
        <button onClick={onCancel} className="btn-outline" data-testid="admin-show-cancel">
          <X size={14} /> {t.admin.shows.cancel}
        </button>
      </div>
    </div>
  );
};

// ============ Team Tab ============
const emptyMember = () => ({
  name_ru: "",
  name_en: "",
  role_ru: "",
  role_en: "",
  bio_ru: "",
  bio_en: "",
  image: "",
  instagram: "",
  order: 0,
});

const TeamTab = () => {
  const { t } = useI18n();
  const [team, setTeam] = useState([]);
  const [editing, setEditing] = useState(null);
  const [draft, setDraft] = useState(emptyMember());

  const load = useCallback(async () => {
    try {
      const r = await api.get("/team");
      setTeam(r.data);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const startEdit = (m) => {
    setEditing(m.id);
    setDraft({ ...m });
  };
  const startNew = () => {
    setEditing("new");
    setDraft(emptyMember());
  };
  const cancel = () => {
    setEditing(null);
  };

  const save = async () => {
    try {
      const payload = { ...draft, order: Number(draft.order) || 0 };
      if (editing === "new") {
        await api.post("/admin/team", payload);
        toast.success("Created");
      } else {
        await api.patch(`/admin/team/${editing}`, payload);
        toast.success("Saved");
      }
      await load();
      cancel();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const remove = async (id) => {
    if (!window.confirm(t.admin.team.confirm_delete)) return;
    try {
      await api.delete(`/admin/team/${id}`);
      toast.success("Deleted");
      await load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  return (
    <div className="space-y-6" data-testid="admin-team">
      <div className="flex justify-end">
        <button onClick={startNew} className="btn-primary" data-testid="admin-team-add">
          <Plus size={16} /> {t.admin.team.add}
        </button>
      </div>

      {editing && (
        <MemberEditor
          draft={draft}
          setDraft={setDraft}
          onSave={save}
          onCancel={cancel}
          t={t}
        />
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {team.map((m) => (
          <div
            key={m.id}
            className="border border-white/15 p-4 flex gap-4"
            data-testid={`admin-team-row-${m.id}`}
          >
            <img
              src={m.image}
              alt={m.name_en}
              className="w-20 h-24 object-cover bg-white/5"
              referrerPolicy="no-referrer"
            />
            <div className="flex-1 min-w-0">
              <div className="font-display text-xl truncate">{m.name_ru}</div>
              <div className="text-[10px] tracking-widest text-[#FFDE00] uppercase mt-1">
                {m.role_ru}
              </div>
              <div className="text-xs text-white/50 mt-2 line-clamp-2">{m.bio_ru}</div>
              <div className="mt-3 flex gap-2">
                <button
                  onClick={() => startEdit(m)}
                  className="px-2 py-1 text-xs border border-white/20 hover:border-[#FFDE00] hover:text-[#FFDE00]"
                  data-testid={`admin-team-edit-${m.id}`}
                >
                  <Pencil size={12} />
                </button>
                <button
                  onClick={() => remove(m.id)}
                  className="px-2 py-1 text-xs border border-white/20 hover:border-red-400 hover:text-red-400"
                  data-testid={`admin-team-delete-${m.id}`}
                >
                  <Trash2 size={12} />
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

const MemberEditor = ({ draft, setDraft, onSave, onCancel, t }) => {
  const upd = (k) => (e) => setDraft({ ...draft, [k]: e.target.value });
  const fields = [
    ["name_ru", "Имя (RU)"],
    ["name_en", "Name (EN)"],
    ["role_ru", "Роль (RU)"],
    ["role_en", "Role (EN)"],
    ["image", "Image URL"],
    ["instagram", "Instagram"],
    ["order", "Order"],
  ];
  return (
    <div className="border border-white/15 p-5 bg-[#161616]/40" data-testid="admin-team-editor">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        {fields.map(([k, label]) => (
          <div key={k}>
            <label className="text-[10px] tracking-widest uppercase text-white/45">{label}</label>
            <input
              value={draft[k] ?? ""}
              onChange={upd(k)}
              className="field-input"
              data-testid={`admin-team-input-${k}`}
            />
          </div>
        ))}
        <div className="md:col-span-2">
          <label className="text-[10px] tracking-widest uppercase text-white/45">Био (RU)</label>
          <textarea value={draft.bio_ru} onChange={upd("bio_ru")} rows={3} className="field-input" />
        </div>
        <div className="md:col-span-2">
          <label className="text-[10px] tracking-widest uppercase text-white/45">Bio (EN)</label>
          <textarea value={draft.bio_en} onChange={upd("bio_en")} rows={3} className="field-input" />
        </div>
      </div>
      <div className="mt-5 flex gap-3">
        <button onClick={onSave} className="btn-primary" data-testid="admin-team-save">
          <Save size={14} /> {t.admin.shows.save}
        </button>
        <button onClick={onCancel} className="btn-outline" data-testid="admin-team-cancel">
          <X size={14} /> {t.admin.shows.cancel}
        </button>
      </div>
    </div>
  );
};

// ============ Bookings Tab ============
const BookingsTab = () => {
  const { t } = useI18n();
  const [items, setItems] = useState([]);

  useEffect(() => {
    api
      .get("/admin/bookings")
      .then((r) => setItems(r.data))
      .catch((e) => toast.error(formatApiError(e)));
  }, []);

  if (items.length === 0)
    return <div className="text-white/40 py-12">{t.admin.bookings.empty}</div>;

  return (
    <div className="border border-white/15" data-testid="admin-bookings">
      {items.map((b) => (
        <div
          key={b.id}
          className="border-b border-white/10 last:border-b-0 p-5 grid grid-cols-12 gap-3"
          data-testid={`admin-booking-${b.id}`}
        >
          <div className="col-span-12 md:col-span-3">
            <div className="font-display text-lg">{b.name}</div>
            <div className="text-xs text-white/55">{b.email}</div>
            {b.organization && (
              <div className="text-xs text-white/40 mt-1">{b.organization}</div>
            )}
          </div>
          <div className="col-span-6 md:col-span-2 text-xs">
            <div className="text-[10px] tracking-widest uppercase text-[#FFDE00]">
              {b.inquiry_type}
            </div>
            {b.city && <div className="text-white/55 mt-1">{b.city}</div>}
          </div>
          <div className="col-span-12 md:col-span-5 text-sm text-white/80 whitespace-pre-wrap">
            {b.message}
          </div>
          <div className="col-span-6 md:col-span-2 text-[11px] text-white/35 text-right">
            {new Date(b.created_at).toLocaleString()}
          </div>
        </div>
      ))}
    </div>
  );
};

// ============ Newsletter Tab ============
const NewsletterTab = () => {
  const { t } = useI18n();
  const [items, setItems] = useState([]);
  useEffect(() => {
    api
      .get("/admin/newsletter")
      .then((r) => setItems(r.data))
      .catch((e) => toast.error(formatApiError(e)));
  }, []);

  if (items.length === 0)
    return <div className="text-white/40 py-12">{t.admin.newsletter.empty}</div>;

  return (
    <div className="border border-white/15" data-testid="admin-newsletter">
      {items.map((n) => (
        <div
          key={n.id}
          className="border-b border-white/10 last:border-b-0 px-5 py-3 flex justify-between items-center"
          data-testid={`admin-newsletter-${n.id}`}
        >
          <span className="text-sm">{n.email}</span>
          <span className="text-[11px] text-white/35">
            {new Date(n.created_at).toLocaleString()}
          </span>
        </div>
      ))}
    </div>
  );
};

// ============ Users Tab (super admin only) ============
const UsersTab = ({ currentUser }) => {
  const { t } = useI18n();
  const [users, setUsers] = useState([]);
  const [busy, setBusy] = useState(null);

  const load = useCallback(async () => {
    try {
      const r = await api.get("/admin/users");
      setUsers(r.data);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const action = async (id, fn, label) => {
    setBusy(id + label);
    try {
      await fn();
      toast.success(label);
      await load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="border border-white/15" data-testid="admin-users">
      {users.map((u) => (
        <div
          key={u.id}
          className="border-b border-white/10 last:border-b-0 p-5 grid grid-cols-12 gap-3 items-center"
          data-testid={`admin-user-row-${u.id}`}
        >
          <div className="col-span-12 md:col-span-4">
            <div className="font-display text-lg">{u.name}</div>
            <div className="text-xs text-white/55">{u.email}</div>
          </div>
          <div className="col-span-6 md:col-span-2 text-[10px] tracking-widest uppercase">
            <span className="text-white/50">{u.role}</span>
          </div>
          <div className="col-span-6 md:col-span-2">
            <span
              className={`text-[10px] tracking-widest uppercase px-2 py-1 border ${
                u.status === "approved"
                  ? "text-green-400 border-green-400/40"
                  : u.status === "rejected"
                  ? "text-red-400 border-red-400/40"
                  : "text-[#FFDE00] border-[#FFDE00]/40"
              }`}
            >
              {t.admin.users.status[u.status] || u.status}
            </span>
          </div>
          <div className="col-span-12 md:col-span-4 flex justify-end gap-2 flex-wrap">
            {u.role !== "super_admin" && u.status !== "approved" && (
              <button
                onClick={() =>
                  action(u.id, () => api.patch(`/admin/users/${u.id}/approve`), "Approved")
                }
                disabled={busy === u.id + "Approved"}
                className="px-3 py-1.5 text-xs border border-green-400/50 text-green-400 hover:bg-green-400 hover:text-black transition-colors"
                data-testid={`admin-user-approve-${u.id}`}
              >
                <Check size={12} /> {t.admin.users.approve}
              </button>
            )}
            {u.role !== "super_admin" && u.status !== "rejected" && (
              <button
                onClick={() =>
                  action(u.id, () => api.patch(`/admin/users/${u.id}/reject`), "Rejected")
                }
                disabled={busy === u.id + "Rejected"}
                className="px-3 py-1.5 text-xs border border-white/20 text-white/80 hover:border-red-400 hover:text-red-400 transition-colors"
                data-testid={`admin-user-reject-${u.id}`}
              >
                <X size={12} /> {t.admin.users.reject}
              </button>
            )}
            {u.role !== "super_admin" && (
              <button
                onClick={async () => {
                  if (!window.confirm(t.admin.users.confirm_delete)) return;
                  await action(u.id, () => api.delete(`/admin/users/${u.id}`), "Deleted");
                }}
                disabled={busy === u.id + "Deleted"}
                className="px-3 py-1.5 text-xs border border-white/20 text-white/80 hover:border-red-400 hover:text-red-400 transition-colors"
                data-testid={`admin-user-delete-${u.id}`}
              >
                <Trash2 size={12} />
              </button>
            )}
          </div>
        </div>
      ))}
    </div>
  );
};

// ============ Main AdminPage ============
const TABS = ["overview", "shows", "team", "bookings", "newsletter", "users"];

const AdminPage = () => {
  const { t, lang, setLang } = useI18n();
  const { user, logout, loading } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("overview");

  useEffect(() => {
    if (!loading && !user) navigate("/login", { replace: true });
  }, [loading, user, navigate]);

  if (loading || !user) {
    return (
      <div className="min-h-screen bg-[#0a0a0a] text-white flex items-center justify-center">
        <Loader2 className="animate-spin" />
      </div>
    );
  }

  const isSuper = user.role === "super_admin";
  const availableTabs = TABS.filter((k) => (k === "users" ? isSuper : true));

  const TAB_ICONS = {
    overview: LayoutDashboard,
    shows: Calendar,
    team: UserSquare2,
    bookings: Inbox,
    newsletter: Mail,
    users: Users,
  };

  return (
    <div className="min-h-screen bg-[#0a0a0a] text-white" data-testid="admin-page">
      {/* Top bar */}
      <header className="border-b border-white/10 bg-black/60 backdrop-blur-xl sticky top-0 z-30">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 h-16 flex items-center justify-between">
          <div className="flex items-center gap-6">
            <Link to="/" className="font-display text-2xl tracking-tight" data-testid="admin-logo">
              TEAM ASTANA<span className="text-[#FFDE00]">.</span>
            </Link>
            <span className="hidden md:inline text-[10px] tracking-widest uppercase text-white/40">
              {t.admin.title}
            </span>
          </div>
          <div className="flex items-center gap-3">
            <div className="hidden md:flex text-xs text-white/60 mr-3">
              {t.admin.welcome}, <span className="text-white ml-1">{user.name}</span>
              <span className="ml-2 text-[10px] uppercase tracking-widest text-[#FFDE00]">
                {user.role}
              </span>
            </div>
            <button
              onClick={() => setLang(lang === "ru" ? "en" : "ru")}
              className="text-xs px-2 py-1 border border-white/15 tracking-widest"
              data-testid="admin-lang-toggle"
            >
              {lang.toUpperCase()}
            </button>
            <HelpButton />
            <Link
              to="/"
              className="hidden md:inline-flex items-center gap-1.5 text-xs tracking-widest uppercase text-white/55 hover:text-[#FFDE00]"
              data-testid="admin-home-link"
            >
              <Home size={14} />
            </Link>
            <button
              onClick={async () => {
                await logout();
                navigate("/");
              }}
              className="inline-flex items-center gap-1.5 text-xs tracking-widest uppercase text-white/70 hover:text-red-400"
              data-testid="admin-logout"
            >
              <LogOut size={14} /> {t.nav.logout}
            </button>
          </div>
        </div>

        {/* Tabs */}
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 -mb-px flex gap-1 overflow-x-auto">
          {availableTabs.map((k) => {
            const Icon = TAB_ICONS[k];
            const active = tab === k;
            return (
              <button
                key={k}
                onClick={() => setTab(k)}
                data-testid={`admin-tab-${k}`}
                className={`inline-flex items-center gap-2 px-4 py-3 text-xs tracking-widest uppercase font-semibold whitespace-nowrap border-b-2 transition-colors ${
                  active
                    ? "border-[#FFDE00] text-[#FFDE00]"
                    : "border-transparent text-white/55 hover:text-white"
                }`}
              >
                <Icon size={14} /> {t.admin.tabs[k]}
              </button>
            );
          })}
        </div>
      </header>

      <main className="max-w-[1400px] mx-auto px-6 md:px-10 py-10">
        {tab === "overview" && <OverviewTab />}
        {tab === "shows" && <ShowsTab />}
        {tab === "team" && <TeamTab />}
        {tab === "bookings" && <BookingsTab />}
        {tab === "newsletter" && <NewsletterTab />}
        {tab === "users" && isSuper && <UsersTab currentUser={user} />}
      </main>
    </div>
  );
};

export default AdminPage;
